import { Button } from '@snitch/ui/button'
import { plural } from '@snitch/ui/lib/format'
import { OtpInput } from '@snitch/ui/otp-input'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { ArrowRight, CircleAlert } from 'lucide-react'
import { useEffect, useState } from 'react'
import { AuthShell } from '../components/auth-shell.tsx'
import { type EmailVerifyResult, sendEmailCode, verifyEmailCode } from '../lib/auth.ts'
import { meQuery, safeNext } from '../lib/session.ts'

interface CodeSearch {
  email: string
  next?: string
}

export const Route = createFileRoute('/login_/code')({
  validateSearch: (search: Record<string, unknown>): CodeSearch => ({
    email: typeof search.email === 'string' ? search.email : '',
    next: typeof search.next === 'string' ? search.next : undefined,
  }),
  beforeLoad: ({ search }) => {
    if (!search.email) throw redirect({ to: '/login' })
  },
  component: Code,
})

const RESEND_SECONDS = 60
const ATTEMPT_FORMS = ['попытка', 'попытки', 'попыток'] as const

function errorText(result: Extract<EmailVerifyResult, { ok: false }>): string {
  switch (result.error) {
    case 'invalid_code': {
      const left = result.attemptsLeft ?? 0
      return `Код не подошёл. Осталось ${left} ${plural(left, ATTEMPT_FORMS)}`
    }
    case 'too_many_attempts':
      return 'Слишком много попыток. Запросите новый код'
    case 'expired':
      return 'Код устарел. Запросите новый'
    case 'rate_limited':
      return 'Слишком часто. Попробуйте через минуту'
    case 'suspended':
      return 'Аккаунт заблокирован. Напишите в поддержку'
    default:
      return 'Не получилось войти. Попробуйте ещё раз'
  }
}

/** "Вход · код из письма" and its wrong-code state. */
function Code() {
  const { email, next } = Route.useSearch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [code, setCode] = useState('')
  const [error, setError] = useState<string>()
  const [resendIn, setResendIn] = useState(RESEND_SECONDS)

  useEffect(() => {
    if (resendIn <= 0) return
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000)
    return () => clearTimeout(t)
  }, [resendIn])

  const verify = useMutation({
    mutationFn: (value: string) => verifyEmailCode(email, value),
    onSuccess: async (result) => {
      if (result.ok) {
        // Fetch, not invalidate: nothing observes "me" on this screen, and the route guard would
        // reuse the cached "signed out" answer and bounce back to the login page.
        await queryClient.fetchQuery({ ...meQuery, staleTime: 0 })
        await navigate({ href: safeNext(next), replace: true })
        return
      }
      setError(errorText(result))
    },
    onError: () => setError('Нет связи. Проверьте интернет и попробуйте ещё раз'),
  })

  const resend = useMutation({
    mutationFn: () => sendEmailCode(email),
    onSuccess: (result) => {
      setCode('')
      setError(undefined)
      setResendIn(result.ok ? result.resendIn : (result.retryAfter ?? RESEND_SECONDS))
      if (!result.ok && result.error !== 'rate_limited') setError('Не получилось отправить код')
    },
  })

  const submit = (value = code) => {
    if (value.length !== 6 || verify.isPending) return
    setError(undefined)
    verify.mutate(value)
  }

  const mm = Math.floor(resendIn / 60)
  const ss = String(resendIn % 60).padStart(2, '0')

  return (
    <AuthShell
      title="Код из письма"
      sub={`Отправили 6 цифр на ${email}. Код действует 10 минут.`}
      back={{ to: '/login', search: { next }, label: 'Все способы входа' }}
      mobileBack
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
      >
        <OtpInput
          value={code}
          autoFocus
          error={Boolean(error)}
          disabled={verify.isPending}
          onChange={(value) => {
            setCode(value)
            if (error) setError(undefined)
          }}
          onComplete={submit}
        />
        {error ? (
          <p role="alert" className="flex items-center gap-2 font-medium text-red text-sm">
            <CircleAlert className="size-4 shrink-0" aria-hidden="true" />
            {error}
          </p>
        ) : null}
        <Button
          variant="primary"
          size="lg"
          block
          type="submit"
          disabled={code.length !== 6 || verify.isPending}
        >
          {verify.isPending ? 'Проверяем…' : 'Войти'}
          <ArrowRight aria-hidden="true" />
        </Button>
        <div className="flex flex-col items-center gap-1.5 pt-1 text-center">
          {resendIn > 0 ? (
            <p className="font-medium text-ink-3 text-sm" aria-live="polite">
              Отправить код ещё раз через {mm}:{ss}
            </p>
          ) : (
            <button
              type="button"
              className="font-semibold text-ink text-sm underline underline-offset-2"
              disabled={resend.isPending}
              onClick={() => resend.mutate()}
            >
              Отправить код ещё раз
            </button>
          )}
          <p className="text-[13px] text-ink-2 leading-[1.4]">
            Письма нет? Проверьте «Спам» или{' '}
            <button
              type="button"
              className="underline underline-offset-2"
              onClick={() => void navigate({ to: '/login', search: { next } })}
            >
              войдите через Telegram
            </button>
          </p>
        </div>
      </form>
    </AuthShell>
  )
}
